import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { ZoomIn, ZoomOut, RotateCcw, Download, Settings } from "lucide-react";
import type {
  TelemetryDataPoint,
  ChannelMetadata,
  TelemetryNote,
} from "@/types";
import { useTelemetryLaps } from "@/hooks/useTelemetry";
import { getChannelColor, type ChannelColorIndices } from "./channelColors";
import { CanvasChart } from "./CanvasChart";

interface TelemetryChartProps {
  data: TelemetryDataPoint[];
  selectedChannels: string[];
  channelColorIndices?: ChannelColorIndices;
  channelMetadata: Record<string, ChannelMetadata>;
  sessionId?: string;
 className?: string;
  // Video synchronization props
  syncCurrentTime?: number; // Current time from video sync
  onTimeUpdate?: (time: number) => void; // Callback when user interacts with chart
  onHoverTimeUpdate?: (time: number | null) => void; // Callback when user hovers over chart
  // Chart control functions
  onGetChartControls?: (controls: {
    zoomIn: () => void;
    zoomOut: () => void;
    scrollLeft: () => void;
    scrollRight: () => void;
    centerOnTime: (time: number) => void;
  }) => void;
  // Callback when chart view is manually changed
  onManualPan?: () => void;
  // Auto-center control
  autoCenter?: boolean;
  onAutoCenterChange?: (enabled: boolean) => void;
  // Grid control
  showGrid?: boolean;
  onShowGridChange?: (enabled: boolean) => void;
  // Chart height control
  chartHeight?: number;
  onChartHeightChange?: (height: number) => void;
  // Chart zoom/viewport control
  chartViewport?: { startTime: number; endTime: number } | null;
  onChartViewportChange?: (
    viewport: { startTime: number; endTime: number } | null,
  ) => void;
  // Telemetry notes functionality
  notes?: TelemetryNote[];
  onNoteClick?: (note: TelemetryNote) => void;
  onNoteCreate?: (timeOffset: number) => void;
  onNoteUpdate?: (noteId: string, newTimeOffset: number) => void;
  selectedNoteId?: string | null;
  notesEnabled?: boolean;
}

export const TelemetryChart: React.FC<TelemetryChartProps> = React.memo(
  ({
    data,
    selectedChannels,
    channelColorIndices,
    channelMetadata,
    sessionId,
 className = "",
    syncCurrentTime,
    onTimeUpdate,
    onHoverTimeUpdate,
    onGetChartControls,
    onManualPan,
    autoCenter = true,
    onAutoCenterChange,
    showGrid = true,
    onShowGridChange,
    chartHeight = 200,
    onChartHeightChange,
    chartViewport = null,
    onChartViewportChange,
    notes = [],
    onNoteClick,
    onNoteCreate,
    onNoteUpdate,
    selectedNoteId,
    notesEnabled = false,
  }) => {
    const { t } = useTranslation(["pages", "common"]);
    const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(800);
    const lastCenteredTimeRef = useRef<number | undefined>(undefined);

    // Fetch lap data if sessionId is provided
    const { data: lapDataResponse } = useTelemetryLaps(
      sessionId || "",
      !!sessionId,
    );
    const lapData = lapDataResponse?.data?.laps;

    // Update container width on resize using ResizeObserver
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth);
        }
      };

      updateWidth();

      // Use ResizeObserver to detect container size changes (for panel resizing)
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === containerRef.current) {
            setContainerWidth(entry.contentRect.width);
          }
        }
      });

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      // Also listen to window resize events as fallback
      window.addEventListener("resize", updateWidth);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateWidth);
      };
    }, []);

    // Initialize zoomDomain from chartViewport on mount
    useEffect(() => {
      if (chartViewport && !zoomDomain) {
        setZoomDomain([chartViewport.startTime, chartViewport.endTime]);
      }
    }, [chartViewport, zoomDomain]);

    // Latest-value refs (#1203): the imperative chart controls below only
    // need the current data/viewport-callback values when they are invoked.
    // Keying the control handler identities on those prop references made
    // the reported controls object churn on every parent re-render with
    // fresh prop identities, and SessionAnalyticsPage wires
    // onGetChartControls straight into setChartControls — re-reporting a
    // fresh object re-rendered the parent, which re-rendered the chart:
    // the same effect↔setState commit loop that starved React Router's
    // back-navigation transition in TrackLayoutVisualization (#1151).
    const dataRef = useRef(data);
    useEffect(() => {
      dataRef.current = data;
    });
    const onChartViewportChangeRef = useRef(onChartViewportChange);
    useEffect(() => {
      onChartViewportChangeRef.current = onChartViewportChange;
    });

    // Custom zoom domain setter that also persists to parent
    const updateZoomDomain = useCallback(
      (newDomain: [number, number] | null) => {
        setZoomDomain(newDomain);
        const viewport = newDomain
          ? { startTime: newDomain[0], endTime: newDomain[1] }
          : null;
        onChartViewportChangeRef.current?.(viewport);
      },
      [],
    );

    const handleZoomIn = useCallback(() => {
      const currentData = dataRef.current;
      if (currentData.length === 0) return;
      const currentDomain = zoomDomain || [
        currentData[0].timestamp,
        currentData[currentData.length - 1].timestamp,
      ];
      const center = (currentDomain[0] + currentDomain[1]) / 2;
      const newRange = (currentDomain[1] - currentDomain[0]) * 0.5;
      updateZoomDomain([center - newRange / 2, center + newRange / 2]);
    }, [zoomDomain, updateZoomDomain]);

    const handleZoomOut = useCallback(() => {
      const currentData = dataRef.current;
      if (currentData.length === 0) return;
      if (!zoomDomain) return;
      const fullRange = [
        currentData[0].timestamp,
        currentData[currentData.length - 1].timestamp,
      ];
      const currentRange = zoomDomain[1] - zoomDomain[0];
      const newRange = Math.min(currentRange * 2, fullRange[1] - fullRange[0]);
      const center = (zoomDomain[0] + zoomDomain[1]) / 2;

      const newStart = Math.max(fullRange[0], center - newRange / 2);
      const newEnd = Math.min(fullRange[1], center + newRange / 2);

      if (newStart === fullRange[0] && newEnd === fullRange[1]) {
        updateZoomDomain(null);
      } else {
        updateZoomDomain([newStart, newEnd]);
      }
    }, [zoomDomain, updateZoomDomain]);

    const handleResetZoom = useCallback(() => {
      updateZoomDomain(null);
    }, [updateZoomDomain]);

    const handleScrollLeft = useCallback(() => {
      const currentData = dataRef.current;
      if (currentData.length === 0) return;
      const currentDomain = zoomDomain || [
        currentData[0].timestamp,
        currentData[currentData.length - 1].timestamp,
      ];
      const range = currentDomain[1] - currentDomain[0];
      const scrollAmount = range * 0.1; // Scroll by 10% of visible range

      const fullRange = [
        currentData[0].timestamp,
        currentData[currentData.length - 1].timestamp,
      ];
      const newStart = Math.max(fullRange[0], currentDomain[0] - scrollAmount);
      const newEnd = Math.max(
        fullRange[0] + range,
        currentDomain[1] - scrollAmount,
      );

      if (newStart !== currentDomain[0] || newEnd !== currentDomain[1]) {
        updateZoomDomain([newStart, newEnd]);
      }
    }, [zoomDomain, updateZoomDomain]);

    const handleScrollRight = useCallback(() => {
      const currentData = dataRef.current;
      if (currentData.length === 0) return;
      const currentDomain = zoomDomain || [
        currentData[0].timestamp,
        currentData[currentData.length - 1].timestamp,
      ];
      const range = currentDomain[1] - currentDomain[0];
      const scrollAmount = range * 0.1; // Scroll by 10% of visible range

      const fullRange = [
        currentData[0].timestamp,
        currentData[currentData.length - 1].timestamp,
      ];
      const newStart = Math.min(
        fullRange[1] - range,
        currentDomain[0] + scrollAmount,
      );
      const newEnd = Math.min(fullRange[1], currentDomain[1] + scrollAmount);

      if (newStart !== currentDomain[0] || newEnd !== currentDomain[1]) {
        updateZoomDomain([newStart, newEnd]);
      }
    }, [zoomDomain, updateZoomDomain]);

    const handleCenterOnTime = useCallback(
      (time: number) => {
        // Auto-centering follows the playhead within the zoomed window. When
        // un-zoomed the full range is already visible, so centering must never
        // create a zoom domain (e.g. after a plain click-to-seek).
        const currentData = dataRef.current;
        if (currentData.length === 0 || !zoomDomain) return;

        const fullRange = [
          currentData[0].timestamp,
          currentData[currentData.length - 1].timestamp,
        ];

        const targetTime = Math.min(
          Math.max(time, fullRange[0]),
          fullRange[1],
        );

        const targetRange = zoomDomain[1] - zoomDomain[0];

        // Center the view on the target time
        let newStart = targetTime - targetRange / 2;
        let newEnd = targetTime + targetRange / 2;

        // Constrain to data bounds
        if (newStart < fullRange[0]) {
          newStart = fullRange[0];
          newEnd = Math.min(fullRange[1], newStart + targetRange);
        }
        if (newEnd > fullRange[1]) {
          newEnd = fullRange[1];
          newStart = Math.max(fullRange[0], newEnd - targetRange);
        }

        // Only update if the time is not already visible in the current view
        const isVisible =
          targetTime >= zoomDomain[0] && targetTime <= zoomDomain[1];
        const hasDomainChanged =
          Math.abs(zoomDomain[0] - newStart) > 0.01 ||
          Math.abs(zoomDomain[1] - newEnd) > 0.01;

        if (!isVisible && hasDomainChanged) {
          updateZoomDomain([newStart, newEnd]);
        }
      },
      [zoomDomain, updateZoomDomain],
    );

    // Stable controls identity (#1203, same pattern as #1201): the handlers
    // only change when the zoom domain actually changes, so this memoized
    // object keeps a stable reference across parent re-renders — even ones
    // passing fresh data/callback identities. The parent's setChartControls
    // then bails out on the identical reference instead of re-rendering, so
    // the effect↔setState starvation loop from #1151 cannot reappear on the
    // chart side. Zoom interactions still propagate: the memo recomputes
    // whenever the zoom state changes.
    const chartControls = useMemo(
      () => ({
        zoomIn: handleZoomIn,
        zoomOut: handleZoomOut,
        scrollLeft: handleScrollLeft,
        scrollRight: handleScrollRight,
        centerOnTime: handleCenterOnTime,
      }),
      [
        handleZoomIn,
        handleZoomOut,
        handleScrollLeft,
        handleScrollRight,
        handleCenterOnTime,
      ],
    );

    // Expose chart controls to parent component
    useEffect(() => {
      if (onGetChartControls) {
        onGetChartControls(chartControls);
      }
    }, [onGetChartControls, chartControls]);

    // Auto-center on video time changes when enabled
    useEffect(() => {
      if (!autoCenter) {
        lastCenteredTimeRef.current = undefined;
        return;
      }

      if (
        syncCurrentTime !== undefined &&
        syncCurrentTime >= 0 &&
        Math.abs((lastCenteredTimeRef.current || 0) - syncCurrentTime) > 0.5
      ) {
        lastCenteredTimeRef.current = syncCurrentTime;
        handleCenterOnTime(syncCurrentTime);
      }
    }, [autoCenter, syncCurrentTime, handleCenterOnTime]);

    const downloadChart = useCallback(() => {
      const canvas = containerRef.current?.querySelector("canvas");
      if (canvas) {
        const link = document.createElement("a");
        link.download = "telemetry-chart.png";
        link.href = canvas.toDataURL();
        link.click();
      }
    }, []);

    if (selectedChannels.length === 0) {
      return (
        <div
          className={`p-8 text-center ${className}`} style={{ background: "#080810" }}
        >
          <div style={{ color: "#4b5563" }}>
            <div className="text-base font-medium mb-2">{t("pages:sessions.telemetry.chart.noChannelsSelected")}</div>
            <div className="text-sm" style={{ color: "#374151" }}>
              {t("pages:sessions.telemetry.chart.selectDataChannelsHint")}
            </div>
          </div>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div
          className={`p-8 text-center ${className}`} style={{ background: "#080810" }}
        >
          <div style={{ color: "#4b5563" }}>
            <div className="text-base font-medium mb-2">{t("pages:sessions.telemetry.chart.noDataAvailable")}</div>
            <div className="text-sm" style={{ color: "#374151" }}>
              {t("pages:sessions.telemetry.chart.noTelemetryForTimeRange")}
            </div>
          </div>
        </div>
      );
    }

    const lapBarHeight = lapData && lapData.length > 0 ? 40 : 0;
    const totalHeight =
      selectedChannels.length * chartHeight + 100 + lapBarHeight;

    return (
      <div
        className={`bg-[#080810] flex flex-col ${className}`}
      >
        {/* Chart Header */}
        <div
          className="flex items-center justify-between flex-shrink-0 sticky top-0 z-10"
          style={{ borderBottom: "1px solid #1a1a2e", background: "#111827", padding: "8px 14px" }}
        >
          {/* Channel legend */}
          <div className="flex items-center gap-4 flex-wrap min-w-0">
            {selectedChannels.map((ch, idx) => {
              const color = getChannelColor(ch, channelColorIndices, idx);
              return (
                <div key={ch} className="flex items-center gap-1.5 min-w-0">
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 5px ${color}80`, flexShrink: 0 }} />
                  <span className="truncate min-w-0" style={{ fontSize: 12, color: "#6b7280" }}>{ch}</span>
                </div>
              );
            })}
          </div>
          {/* Controls */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onAutoCenterChange?.(!autoCenter)}
              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium min-h-11 lg:min-h-0"
              style={autoCenter
                ? { background: "#052e16", color: "#4ade80", border: "1px solid #14532d" }
                : { background: "#1f2937", color: "#6b7280", border: "1px solid #374151" }}
              title={t("pages:sessions.telemetry.chart.toggleAutoCenter")}
            >
              {t("pages:sessions.telemetry.chart.auto")}{autoCenter ? "✓" : ""}
            </button>
            <div style={{ width: 1, height: 16, background: "#1f2937", margin: "0 2px" }} />
            <select
              value={chartHeight}
              onChange={(e) => onChartHeightChange?.(Number(e.target.value))}
              className="text-xs rounded px-2 py-1 h-11 lg:h-auto"
              style={{ background: "#1f2937", color: "#6b7280", border: "1px solid #374151" }}
            >
              <option value={120}>{t("pages:sessions.telemetry.chart.heightCompact")}</option>
              <option value={150}>{t("pages:sessions.telemetry.chart.heightSmall")}</option>
              <option value={200}>{t("pages:sessions.telemetry.chart.heightMedium")}</option>
              <option value={250}>{t("pages:sessions.telemetry.chart.heightLarge")}</option>
              <option value={300}>{t("pages:sessions.telemetry.chart.heightXLarge")}</option>
            </select>
            <button onClick={() => onShowGridChange?.(!showGrid)}
              className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5"
              style={{ color: showGrid ? "#4ade80" : "#4b5563" }}
              title={t("pages:sessions.telemetry.chart.toggleGrid")}>
              <Settings className="h-3.5 w-3.5" />
            </button>
            <button onClick={handleZoomIn}
              className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5" style={{ color: "#4b5563" }} title={t("pages:sessions.telemetry.chart.zoomIn")}>
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <button onClick={handleZoomOut}
              className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5" style={{ color: zoomDomain ? "#4b5563" : "#2d2d3a" }}
              disabled={!zoomDomain}>
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <button onClick={handleResetZoom}
              className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5" style={{ color: zoomDomain ? "#4b5563" : "#2d2d3a" }}
              disabled={!zoomDomain} title={t("pages:sessions.telemetry.chart.resetZoom")}>
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button onClick={downloadChart}
              className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5" style={{ color: "#4b5563" }} title={t("pages:sessions.telemetry.chart.downloadPng")}>
              <Download className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Canvas Chart */}
        <div ref={containerRef} style={{ height: totalHeight }}>
          <CanvasChart
            data={data}
            selectedChannels={selectedChannels}
            channelColorIndices={channelColorIndices}
            channelMetadata={channelMetadata}
            chartHeight={chartHeight}
            showGrid={showGrid}
            zoomDomain={zoomDomain}
            onZoomChange={updateZoomDomain}
            width={containerWidth}
            height={totalHeight}
            lapData={lapData}
            syncCurrentTime={syncCurrentTime}
            onTimeUpdate={onTimeUpdate}
            onHoverTimeUpdate={onHoverTimeUpdate}
            onManualPan={onManualPan}
            autoCenter={autoCenter}
            onAutoCenterChange={onAutoCenterChange}
            notes={notes}
            onNoteClick={onNoteClick}
            onNoteCreate={onNoteCreate}
            onNoteUpdate={onNoteUpdate}
            selectedNoteId={selectedNoteId}
            notesEnabled={notesEnabled}
          />
        </div>
      </div>
    );
  },
);
